// Formatting helpers local to the Products feature. Duplicated from
// features/reports/format.ts's identical functions rather than imported —
// that file's own header documents why every prior web goal (reports itself,
// stores-transfers/format.ts, silo-counts/utils.ts, sales-invoicing/format.ts)
// keeps its own small copy instead of a shared lib/format.ts: each feature
// stays independently ownable, and these are a few lines each.
//
// Money is always integer ZAR cents (CLAUDE.md invariant); mass/qty are
// decimal-string numerics off the wire.

/** ZAR cents (bigint, its wire decimal-string form, or a plain number) -> "R1,234.56". */
export function formatMoneyCents(cents: bigint | string | number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  const negative = n < 0;
  const rands = Math.abs(n) / 100;
  const formatted = rands.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${negative ? '-' : ''}R${formatted}`;
}

/** A per-unit COST RATE (not a total — those are already whole-Rand `valueCents`
 * fields, formatted with `formatMoneyCents` and left untouched). Stuart thinks
 * in Rand-per-tonne, not cents-per-kg (2026-08-26 ruling): a KG product's cost
 * is shown as Rand-per-TONNE (input cents/kg x 1000kg/t / 100c/R = x10); every
 * other base UOM (EACH, L) keeps the existing cents-per-unit -> "R.../unit"
 * convention (PurchaseOrderRegister.tsx's `R${centsToRand(...)} / unit` line
 * price). Input is always the raw per-base-unit cost in ZAR cents — same trio
 * of wire shapes `formatMoneyCents` accepts (bigint, decimal-string, number). */
export function formatCostPerUom(cents: bigint | string | number | null | undefined, baseUom: string): string {
  if (cents === null || cents === undefined) return '—';
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  const negative = n < 0;
  const abs = Math.abs(n);
  if (baseUom === 'KG') {
    const randsPerTonne = abs * 10;
    const formatted = randsPerTonne.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${negative ? '-' : ''}R${formatted} / t`;
  }
  const rands = abs / 100;
  const formatted = rands.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${negative ? '-' : ''}R${formatted} / unit`;
}

/** Plain quantity (not necessarily kg — some products are EACH/L), trims to
 * 3dp with thousands separators. Keeps the sign for negative (outbound)
 * ledger deltas. */
export function formatQty(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-ZA', { maximumFractionDigits: 3 });
}

export function formatDate(value: Date | string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDateTime(value: Date | string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
